import { useEffect, useRef, useState } from 'react';
import { Centrifuge } from 'centrifuge';
import { webSocketUrl } from '@/app/common/api/config';
import { useGenerateTokenMutation } from '@/app/common/api/socketApi';

export type CentrifugeProgressMessage = {
  finished?: boolean;
  progress_percentage?: number;
  /** Clustering / numeric percent payload */
  progress?: number;
  /** Clustering history status (2 queue, 4 processing, 6 done, 3 error, 49 stopped) */
  status?: number;
  /** Job completion (indexer backend when finished: true) */
  state?: string;
  jobKind?: 'inspect' | 'google_index' | 'indexnow';
  indexStatusSummary?: { indexed: number; notIndexed: number };
  /** One URL just saved — same shape as GET job → results[] */
  latestResult?: import('@/app/Indexer/api/types').ProjectResult;
  successCount?: number;
  failedCount?: number;
  /** تست ۲۰۰ — آخرین نتیجه HEAD */
  headCheckResult?: import('@/app/Indexer/api/types').UrlHeadCheckResult;
  /** Chat skill execution progress */
  phase?: string;
  message?: string;
  isDone?: boolean;
  executionStatus?: string;
  summary?: string | null;
  chart?: unknown;
  okCount?: number;
  processed?: number;
  total?: number;
};

type UseCentrifugeProgressOptions = {
  channelId: string | undefined;
  active: boolean;
  onComplete?: (data: CentrifugeProgressMessage) => void;
  onProgress?: (data: CentrifugeProgressMessage) => void;
};

function normalizeProgressMessage(
  raw: unknown
): CentrifugeProgressMessage | null {
  if (!raw || typeof raw !== 'object') return null;

  const outer = raw as CentrifugeProgressMessage & {
    data?: CentrifugeProgressMessage;
  };

  if (
    outer.progress !== undefined ||
    outer.status !== undefined ||
    outer.progress_percentage !== undefined ||
    outer.finished !== undefined ||
    outer.state !== undefined ||
    outer.jobKind !== undefined ||
    outer.indexStatusSummary !== undefined ||
    outer.latestResult !== undefined ||
    outer.successCount !== undefined ||
    outer.failedCount !== undefined ||
    outer.headCheckResult !== undefined ||
    outer.okCount !== undefined ||
    outer.processed !== undefined ||
    outer.total !== undefined ||
    outer.phase !== undefined ||
    outer.message !== undefined ||
    outer.isDone !== undefined ||
    outer.executionStatus !== undefined ||
    outer.summary !== undefined ||
    outer.chart !== undefined
  ) {
    return outer;
  }

  if (outer.data && typeof outer.data === 'object') {
    return outer.data;
  }

  return null;
}

function getProgressPercentage(data: CentrifugeProgressMessage | null): number {
  if (!data) return 0;
  const value =
    data.progress_percentage ??
    (typeof data.progress === 'number' ? data.progress : undefined);
  if (typeof value !== 'number' || Number.isNaN(value)) return 0;
  return Math.min(100, Math.max(0, Math.floor(value)));
}

function isProgressComplete(data: CentrifugeProgressMessage | null): boolean {
  if (!data) return false;
  if (data.finished === true || data.isDone === true) return true;
  if (data.status === 6) return true;
  return getProgressPercentage(data) === 100;
}

export function useCentrifugeProgress({
  channelId,
  active,
  onComplete,
  onProgress
}: UseCentrifugeProgressOptions) {
  const [token, setToken] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const [progressData, setProgressData] =
    useState<CentrifugeProgressMessage | null>(null);
  const [generateToken] = useGenerateTokenMutation();
  const hasCompletedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const onProgressRef = useRef(onProgress);
  onProgressRef.current = onProgress;

  const progressPercentage = getProgressPercentage(progressData);
  const isComplete = isProgressComplete(progressData);

  useEffect(() => {
    if (!active || !channelId) {
      setToken(null);
      setConnected(false);
      setProgressData(null);
      hasCompletedRef.current = false;
      return;
    }

    hasCompletedRef.current = false;

    const fetchToken = async () => {
      try {
        const response = await generateToken({
          id: channelId,
          channels: [`personal:#${channelId}`]
        }).unwrap();
        setToken(response.data);
      } catch (err) {
        console.error('Error fetching socket token:', err);
      }
    };

    fetchToken();
  }, [active, channelId, generateToken]);

  useEffect(() => {
    if (!active || !token || !channelId) return;

    const centrifugo = new Centrifuge(webSocketUrl, {
      getToken: async () => token
    });

    centrifugo.on('connected', () => setConnected(true));
    centrifugo.on('disconnected', () => setConnected(false));

    centrifugo.on('publication', message => {
      const data = normalizeProgressMessage(message?.data);
      if (!data) return;

      setProgressData(data);
      onProgressRef.current?.(data);

      if (isProgressComplete(data) && !hasCompletedRef.current) {
        hasCompletedRef.current = true;
        centrifugo.disconnect();
        onCompleteRef.current?.(data);
      }
    });

    centrifugo.connect();

    return () => {
      centrifugo.disconnect();
    };
  }, [active, token, channelId]);

  return {
    connected,
    progressData,
    progressPercentage,
    status: progressData?.status,
    isComplete
  };
}
